import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';

import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { PortalServicoService } from '@/core/api/sicom-api/portal/servico/portal-servico.service';
import { PortalServico } from '@/core/api/sicom-api/portal/servico/portal-servico.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';

import { SeletorIcone } from '../seletor-icone/seletor-icone';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-servico-detalhe',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, DialogModule, ButtonModule, InputTextModule, ToggleSwitchModule, SelectEstado, SeletorIcone, CanDirective],
    templateUrl: './servico-detalhe.html',
    styleUrl: './servico-detalhe.scss'
})
/** Modal de novo/edição de serviço exibido no portal (WEB_SERVICO). */
export class ServicoDetalhe {
    private readonly fb = inject(FormBuilder);
    private readonly service = inject(PortalServicoService);
    private readonly messageService = inject(MessageService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);

    /** Visibilidade do modal (two-way). */
    readonly visivel = model<boolean>(false);
    /** Serviço em edição, ou `null` para cadastro novo. */
    readonly servico = input<PortalServico | null>(null);
    /** UF padrão ao cadastrar um novo serviço. */
    readonly ufPadrao = input<string>('');
    /** Emite após salvar com sucesso (para a lista recarregar). */
    readonly salvo = output<void>();

    protected readonly roleServicosEditar = PERMISSOES.configuracoes.servicos.editar;

    protected readonly salvando = signal(false);
    protected readonly seletorAberto = signal(false);

    /** UF selecionada (espelha o control) para reagir ao mostrar/ocultar o Divulgar sob OnPush. */
    protected readonly ufSelecionada = signal('');
    /** Divulgar só se aplica ao CFM (BR). */
    protected readonly mostrarDivulgar = computed(() => this.ufSelecionada() === 'BR');

    /**
     * Regional não escolhe UF: este select define a UF do serviço gravado, e o backend só aceita a
     * do operador. Travado, o formulário deixa de oferecer uma escolha que terminaria em 403.
     */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    private codigo: number | null = null;

    protected readonly form = this.fb.nonNullable.group({
        servico: ['', [Validators.required, Validators.maxLength(50)]],
        descricao: ['', [Validators.required, Validators.maxLength(150)]],
        url: ['', [Validators.required, Validators.maxLength(500)]],
        uf: ['', [Validators.required, Validators.maxLength(2)]],
        icon: ['', [Validators.required, Validators.maxLength(40)]],
        divulga: [false]
    });

    constructor() {
        // Preenche (edição) ou reseta (novo) sempre que o modal abre.
        effect(() => {
            if (!this.visivel()) {
                return;
            }
            const item = this.servico();
            if (item) {
                this.codigo = item.codigo ?? null;
                const uf = (item.uf ?? '').toUpperCase();
                this.form.reset({
                    servico: item.servico ?? '',
                    descricao: item.descricao ?? '',
                    url: item.url ?? '',
                    uf,
                    icon: item.icon ?? '',
                    divulga: uf === 'BR' ? !!item.divulga : false
                });
                this.ufSelecionada.set(uf);
            } else {
                this.codigo = null;
                // Regional: o novo serviço nasce na UF dele — a única que o backend aceita gravar.
                const uf = this.ufOperador() || (this.ufPadrao() ?? '').toUpperCase();
                this.form.reset({ servico: '', descricao: '', url: '', uf, icon: '', divulga: false });
                this.ufSelecionada.set(uf);
            }
        });
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected get tituloModal(): string {
        return this.servico() ? 'Editar serviço' : 'Novo serviço';
    }

    protected get iconeAtual(): string {
        return this.form.controls.icon.value;
    }

    protected onIconeSelecionado(icone: string): void {
        this.form.controls.icon.setValue(icone);
        this.form.controls.icon.markAsDirty();
    }

    protected onUfChange(uf: string): void {
        const normalizada = (uf ?? '').toUpperCase();
        this.form.controls.uf.setValue(normalizada);
        this.ufSelecionada.set(normalizada);
        // Fora do CFM (BR) não há divulgação.
        if (normalizada !== 'BR') {
            this.form.controls.divulga.setValue(false);
        }
    }

    protected abrirSeletor(): void {
        this.seletorAberto.set(true);
    }

    protected salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        this.salvando.set(true);
        const v = this.form.getRawValue();
        const uf = v.uf.toUpperCase();
        const payload: PortalServico = {
            codigo: this.codigo,
            uf,
            servico: v.servico,
            descricao: v.descricao,
            icon: v.icon,
            url: v.url,
            divulga: uf === 'BR' ? v.divulga : false
        };
        this.service
            .salvar(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Serviço salvo', detail: 'O serviço foi salvo com sucesso.' });
                    this.salvando.set(false);
                    this.salvo.emit();
                    this.visivel.set(false);
                },
                error: () => {
                    this.salvando.set(false);
                }
            });
    }

    protected fechar(): void {
        this.visivel.set(false);
    }
}
